/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  EMAProviderOrder,
  EMAProviderOrder$inboundSchema,
} from "./emaproviderorder.js";

/**
 * A remote_session_issuer record — upstream Authorization Server identity that Speakeasy speaks OAuth to.
 */
export type RemoteSessionIssuer = {
  /**
   * Upstream authorization endpoint.
   */
  authorizationEndpoint?: string | undefined;
  /**
   * Advertised grant profiles; metadata evidence is not client authorization or user access.
   */
  authorizationGrantProfilesSupported?: Array<string> | undefined;
  /**
   * Whether the issuer includes the RFC 9207 iss parameter in authorization responses. Null until discovery captures the field.
   */
  authorizationResponseIssParameterSupported?: boolean | undefined;
  /**
   * Whether the issuer supports OpenID Connect Back-Channel Logout. Null until discovery captures the field.
   */
  backchannelLogoutSupported?: boolean | undefined;
  /**
   * Claims the issuer can return in ID tokens and from userinfo. Null until discovery captures the field; an empty array means the field was captured and the issuer advertises none.
   */
  claimsSupported?: Array<string> | null | undefined;
  /**
   * Whether the issuer accepts a Client ID Metadata Document URL as client_id (OAuth CIMD draft).
   */
  clientIdMetadataDocumentSupported: boolean;
  /**
   * URL of OAuth client setup documentation shown when creating clients. Manually set, not RFC 8414; null when unset.
   */
  clientSetupDocumentationUrl?: string | undefined;
  /**
   * PKCE code challenge methods advertised by the issuer (RFC 8414 code_challenge_methods_supported). Null when neither discovery nor an operator has captured the field for this issuer yet; an empty array means the field was captured and the issuer advertises no methods.
   */
  codeChallengeMethodsSupported?: Array<string> | null | undefined;
  createdAt: Date;
  /**
   * The EMA providers for one vendor authorization server, in the order identity chaining tries them. Each provider that can serve is tried in turn, with short bounded retries for transient failures, until one succeeds.
   */
  emaProviders?: EMAProviderOrder | undefined;
  grantTypesSupported?: Array<string> | undefined;
  /**
   * The remote_session_issuer id.
   */
  id: string;
  /**
   * JWS algorithms the issuer signs ID tokens with. Null until discovery captures the field; an empty array means the field was captured and the issuer advertises none.
   */
  idTokenSigningAlgValuesSupported?: Array<string> | null | undefined;
  /**
   * RFC 7662 token introspection endpoint. Null when not advertised or not yet captured by discovery.
   */
  introspectionEndpoint?: string | undefined;
  /**
   * Client authentication methods the introspection endpoint accepts. Null until discovery captures the field; an empty array means the field was captured and the issuer advertises none.
   */
  introspectionEndpointAuthMethodsSupported?: Array<string> | null | undefined;
  /**
   * Issuer URL; matches the iss claim.
   */
  issuer: string;
  /**
   * When the persisted JWK Set becomes stale under the upstream cache policy. Null until the first successful refresh.
   */
  jwksCacheExpiresAt?: Date | undefined;
  /**
   * When Speakeasy last successfully fetched or revalidated the JWK Set. Null until the first successful refresh.
   */
  jwksFetchedAt?: Date | undefined;
  /**
   * Upstream JWKS URI; null when not advertised.
   */
  jwksUri?: string | undefined;
  /**
   * Optional logo asset id; null when unset.
   */
  logoAssetId?: string | undefined;
  /**
   * Optional display name; null when unset.
   */
  name?: string | undefined;
  /**
   * When true, may unlock OIDC-aware behaviour.
   */
  oidc: boolean;
  /**
   * When true, a login that would otherwise request the authorization server's whole scopes_supported omits the scope parameter so the server applies its default. Null when unset.
   */
  omitScopeFallback?: boolean | undefined;
  /**
   * RFC 8414 op_policy_uri; the issuer's client data-usage policy. Null when not advertised.
   */
  opPolicyUri?: string | undefined;
  /**
   * RFC 8414 op_tos_uri; the issuer's terms of service. Null when not advertised.
   */
  opTosUri?: string | undefined;
  /**
   * The owning organization id. Empty for legacy rows not yet backfilled.
   */
  organizationId: string;
  /**
   * When true, the MCP client registers and transacts directly with this issuer.
   */
  passthrough: boolean;
  /**
   * The owning project id. Empty for organization-level issuers.
   */
  projectId: string;
  /**
   * Upstream RFC 7591 registration endpoint; null for issuers without DCR.
   */
  registrationEndpoint?: string | undefined;
  /**
   * Whether the issuer accepts the RFC 8707 resource parameter, as an operator stated it. Null when unset; false omits the parameter on every grant.
   */
  resourceIndicatorSupported?: boolean | undefined;
  responseTypesSupported?: Array<string> | undefined;
  /**
   * Upstream RFC 7009 revocation endpoint; null when the issuer advertises none.
   */
  revocationEndpoint?: string | undefined;
  /**
   * Operator-pinned scope request, sent verbatim on the upstream authorize redirect in place of the resolved scope set. Null when unset.
   */
  scopeOverride?: Array<string> | null | undefined;
  scopesSupported?: Array<string> | undefined;
  /**
   * RFC 8414 service_documentation; developer documentation for the issuer. Null when not advertised.
   */
  serviceDocumentation?: string | undefined;
  /**
   * Project-unique slug.
   */
  slug: string;
  /**
   * Upstream token endpoint.
   */
  tokenEndpoint?: string | undefined;
  tokenEndpointAuthMethodsSupported?: Array<string> | undefined;
  /**
   * When set, calls to this issuer's OAuth endpoints ride this MCP tunnel instead of dialing directly.
   */
  tunneledMcpServerId?: string | undefined;
  updatedAt: Date;
  /**
   * OpenID Connect userinfo endpoint. Null when not advertised or not yet captured by discovery.
   */
  userinfoEndpoint?: string | undefined;
};

/** @internal */
export const RemoteSessionIssuer$inboundSchema: z.ZodMiniType<
  RemoteSessionIssuer,
  unknown
> = z.pipe(
  z.object({
    authorization_endpoint: z.optional(z.string()),
    authorization_grant_profiles_supported: z.optional(z.array(z.string())),
    authorization_response_iss_parameter_supported: z.optional(z.boolean()),
    backchannel_logout_supported: z.optional(z.boolean()),
    claims_supported: z.optional(z.nullable(z.array(z.string()))),
    client_id_metadata_document_supported: z.boolean(),
    client_setup_documentation_url: z.optional(z.string()),
    code_challenge_methods_supported: z.optional(
      z.nullable(z.array(z.string())),
    ),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    ema_providers: z.optional(EMAProviderOrder$inboundSchema),
    grant_types_supported: z.optional(z.array(z.string())),
    id: z.string(),
    id_token_signing_alg_values_supported: z.optional(
      z.nullable(z.array(z.string())),
    ),
    introspection_endpoint: z.optional(z.string()),
    introspection_endpoint_auth_methods_supported: z.optional(
      z.nullable(z.array(z.string())),
    ),
    issuer: z.string(),
    jwks_cache_expires_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    jwks_fetched_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    jwks_uri: z.optional(z.string()),
    logo_asset_id: z.optional(z.string()),
    name: z.optional(z.string()),
    oidc: z.boolean(),
    omit_scope_fallback: z.optional(z.boolean()),
    op_policy_uri: z.optional(z.string()),
    op_tos_uri: z.optional(z.string()),
    organization_id: z.string(),
    passthrough: z.boolean(),
    project_id: z.string(),
    registration_endpoint: z.optional(z.string()),
    resource_indicator_supported: z.optional(z.boolean()),
    response_types_supported: z.optional(z.array(z.string())),
    revocation_endpoint: z.optional(z.string()),
    scope_override: z.optional(z.nullable(z.array(z.string()))),
    scopes_supported: z.optional(z.array(z.string())),
    service_documentation: z.optional(z.string()),
    slug: z.string(),
    token_endpoint: z.optional(z.string()),
    token_endpoint_auth_methods_supported: z.optional(z.array(z.string())),
    tunneled_mcp_server_id: z.optional(z.string()),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    userinfo_endpoint: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "authorization_endpoint": "authorizationEndpoint",
      "authorization_grant_profiles_supported":
        "authorizationGrantProfilesSupported",
      "authorization_response_iss_parameter_supported":
        "authorizationResponseIssParameterSupported",
      "backchannel_logout_supported": "backchannelLogoutSupported",
      "claims_supported": "claimsSupported",
      "client_id_metadata_document_supported":
        "clientIdMetadataDocumentSupported",
      "client_setup_documentation_url": "clientSetupDocumentationUrl",
      "code_challenge_methods_supported": "codeChallengeMethodsSupported",
      "created_at": "createdAt",
      "ema_providers": "emaProviders",
      "grant_types_supported": "grantTypesSupported",
      "id_token_signing_alg_values_supported":
        "idTokenSigningAlgValuesSupported",
      "introspection_endpoint": "introspectionEndpoint",
      "introspection_endpoint_auth_methods_supported":
        "introspectionEndpointAuthMethodsSupported",
      "jwks_cache_expires_at": "jwksCacheExpiresAt",
      "jwks_fetched_at": "jwksFetchedAt",
      "jwks_uri": "jwksUri",
      "logo_asset_id": "logoAssetId",
      "omit_scope_fallback": "omitScopeFallback",
      "op_policy_uri": "opPolicyUri",
      "op_tos_uri": "opTosUri",
      "organization_id": "organizationId",
      "project_id": "projectId",
      "registration_endpoint": "registrationEndpoint",
      "resource_indicator_supported": "resourceIndicatorSupported",
      "response_types_supported": "responseTypesSupported",
      "revocation_endpoint": "revocationEndpoint",
      "scope_override": "scopeOverride",
      "scopes_supported": "scopesSupported",
      "service_documentation": "serviceDocumentation",
      "token_endpoint": "tokenEndpoint",
      "token_endpoint_auth_methods_supported":
        "tokenEndpointAuthMethodsSupported",
      "tunneled_mcp_server_id": "tunneledMcpServerId",
      "updated_at": "updatedAt",
      "userinfo_endpoint": "userinfoEndpoint",
    });
  }),
);

export function remoteSessionIssuerFromJSON(
  jsonString: string,
): SafeParseResult<RemoteSessionIssuer, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RemoteSessionIssuer$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RemoteSessionIssuer' from JSON`,
  );
}
